/**
 * Role-Based Access Control (RBAC) Types
 */

export type UserRole = 'USER' | 'SUB_ADMIN' | 'ADMIN'

export interface RolePermissions {
  // User Management
  canViewUsers: boolean
  canEditUsers: boolean
  canDeleteUsers: boolean
  canChangeUserRoles: boolean
  
  // Property Management
  canViewAllProperties: boolean
  canEditAllProperties: boolean
  canDeleteAllProperties: boolean
  canApproveProperties: boolean
  
  // Subscription Management
  canManageSubscriptions: boolean
  canViewPayments: boolean
  
  // System Settings
  canAccessAdminPanel: boolean
  canManageSystemSettings: boolean
  canViewAnalytics: boolean
  canExportData: boolean
  
  // Content Management
  canManageBlog: boolean
  canManagePages: boolean
  
  // Support
  canViewAllTickets: boolean
  canRespondToTickets: boolean
}

export interface UserWithRole {
  uid: string
  email: string
  role: UserRole
  assignedBy?: string // UID of admin who assigned the role
  assignedAt?: string // ISO timestamp
}

// Role configurations
// IMPORTANT: Only ADMIN role has full access to admin panel and subscriptions management
// SUB_ADMIN has limited permissions and cannot access /admin routes
export const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  USER: {
    canViewUsers: false,
    canEditUsers: false,
    canDeleteUsers: false,
    canChangeUserRoles: false,
    canViewAllProperties: false,
    canEditAllProperties: false,
    canDeleteAllProperties: false,
    canApproveProperties: false,
    canManageSubscriptions: false,
    canViewPayments: false,
    canAccessAdminPanel: false,
    canManageSystemSettings: false,
    canViewAnalytics: false,
    canExportData: false,
    canManageBlog: false,
    canManagePages: false,
    canViewAllTickets: false,
    canRespondToTickets: false
  },
  SUB_ADMIN: {
    canViewUsers: true,
    canEditUsers: false,
    canDeleteUsers: false,
    canChangeUserRoles: false,
    canViewAllProperties: true,
    canEditAllProperties: true,
    canDeleteAllProperties: false,
    canApproveProperties: true,
    canManageSubscriptions: false,
    canViewPayments: false,
    canAccessAdminPanel: false, // Only ADMIN can access admin panel
    canManageSystemSettings: false,
    canViewAnalytics: false,
    canExportData: false,
    canManageBlog: false,
    canManagePages: false,
    canViewAllTickets: true,
    canRespondToTickets: true
  },
  ADMIN: {
    canViewUsers: true,
    canEditUsers: true,
    canDeleteUsers: true,
    canChangeUserRoles: true,
    canViewAllProperties: true,
    canEditAllProperties: true,
    canDeleteAllProperties: true,
    canApproveProperties: true,
    canManageSubscriptions: true,
    canViewPayments: true,
    canAccessAdminPanel: true,
    canManageSystemSettings: true,
    canViewAnalytics: true,
    canExportData: true,
    canManageBlog: true,
    canManagePages: true,
    canViewAllTickets: true,
    canRespondToTickets: true
  }
}

// Predefined admin emails
export const ADMIN_EMAILS = [
  'mapbds.info@gmail.com',
  'admin@mapbds.com',
  'levinhtuyen210@gmail.com'

  // Add more admin emails here
]

export const SUB_ADMIN_EMAILS = [
  'support@mapbds.com',
  // Add more sub-admin emails here
]

/**
 * Get role based on email
 */
export const getRoleByEmail = (email: string): UserRole => {
  const normalizedEmail = email.toLowerCase().trim()
  
  if (ADMIN_EMAILS.includes(normalizedEmail)) {
    return 'ADMIN'
  }
  
  if (SUB_ADMIN_EMAILS.includes(normalizedEmail)) {
    return 'SUB_ADMIN'
  }
  
  return 'USER'
}

/**
 * Check if email is admin or sub-admin
 */
export const isAdminEmail = (email: string): boolean => {
  const normalizedEmail = email.toLowerCase().trim()
  return ADMIN_EMAILS.includes(normalizedEmail) || SUB_ADMIN_EMAILS.includes(normalizedEmail)
}

/**
 * Get role display name
 */
export const getRoleDisplayName = (role: UserRole): string => {
  const roleNames: Record<UserRole, string> = {
    USER: 'Người dùng',
    SUB_ADMIN: 'Quản trị viên phụ',
    ADMIN: 'Quản trị viên'
  }
  return roleNames[role]
}

/**
 * Get role badge color
 */
export const getRoleBadgeColor = (role: UserRole): string => {
  const colors: Record<UserRole, string> = {
    USER: 'bg-gray-100 text-gray-800',
    SUB_ADMIN: 'bg-blue-100 text-blue-800',
    ADMIN: 'bg-purple-100 text-purple-800'
  }
  return colors[role]
}
